import {
  AutoCompleteShipInfo,
  extractTargetInfos,
  fillMissingFieldsFromSearch,
  getOverlayMP,
  GetOverlayProps,
  getShipInfo,
  getTargetInfoByMMSI,
  InvalidMMSIError,
  useFindTargetByProperties,
  useSession,
} from '@ais-ui/websockets';
import type { FindTargetResult } from '@ais-ui/websockets';
import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import {
  addPulseToCoordinate,
  getDimensionsFromMap,
  smartZoomTo,
  useOpenLayersContext,
} from '@ais-ui/react-openlayers';
import { fromLonLat } from 'ol/proj';
import { useShipSelection } from './contexts/ShipSelectionContext';
import { useTimeZoneContext } from '@ais-ui/utils';
import { useToast, useIsMobile } from '@ais-ui/ui-core';
import { useLocalization } from '@ais-ui/locale-management';
import { useSelectionState } from '../../components/SelectionStateProvider';
import {
  addRemoteZoomBreadcrumb,
  reportRemoteZoomFailure,
  useReportRemoteFindTargetOutcome,
} from './remoteZoomTelemetry';

function useOverlayAndMap(
  findTargetResult: FindTargetResult | null,
  zoomToShip: AutoCompleteShipInfo | null,
) {
  const [overlayProps, setOverlayProps] = useState<GetOverlayProps | null>(
    null,
  );
  const context = useOpenLayersContext();
  const { toast } = useToast();
  const { messages } = useLocalization();

  // Read via ref so telemetry doesn't change when the zoom effect re-runs
  const zoomToShipRef = useRef(zoomToShip);
  useEffect(() => {
    zoomToShipRef.current = zoomToShip;
  }, [zoomToShip]);

  useEffect(() => {
    if (!findTargetResult) {
      setOverlayProps(null);
      return;
    }

    if (!findTargetResult.found) {
      const description =
        findTargetResult.reason === 'no-position'
          ? messages.shipSearchNoPositionAvailable
          : messages.shipSearchNoTargetFound;

      toast({
        title: messages.genericToastTitleWarning,
        description,
        variant: 'warning',
      });
      setOverlayProps(null);
      return;
    }

    // TypeScript narrows to FindTargetFound — lat/lon are guaranteed finite
    const coordinate = fromLonLat([findTargetResult.lon, findTargetResult.lat]);
    smartZoomTo({ coordinate, map: context.map, animate: false });
    if (zoomToShipRef.current?.source === 'remote') {
      addRemoteZoomBreadcrumb('zoomed to target', {
        mmsi: zoomToShipRef.current.mmsi,
        lat: findTargetResult.lat,
        lon: findTargetResult.lon,
      });
    }

    const dimensions = getDimensionsFromMap(
      context.map,
      context.displayProjection,
      context.dataProjection,
    );

    if (dimensions) {
      const { center, zoomLevel, size, northEast, southWest } = dimensions;
      setOverlayProps({
        centerLat: center.lat,
        centerLon: center.lng,
        zoomLevel: zoomLevel,
        width: size.width,
        height: size.height,
        leftLon: northEast.lng,
        rightLon: southWest.lng,
      });
    } else {
      reportRemoteZoomFailure(zoomToShipRef.current, 'no-map-dimensions');
    }

    addPulseToCoordinate(context.map, coordinate);
  }, [findTargetResult, context]);

  return overlayProps;
}

function useFetchShipInfo(
  isFetching: boolean,
  setIsFetching: (value: boolean) => void,
) {
  const { setTargetInfoDetailsList } = useSelectionState();
  const isMobile = useIsMobile();

  const { zoomToShip, setZoomToShip } = useShipSelection();
  const { olSession } = useSession();
  const [fetchedShipInfoResult, setFetchedShipInfoResult] = useState(null);
  const { toast } = useToast();
  const { messages } = useLocalization();
  const shipAtFetchStart = useRef<AutoCompleteShipInfo | null>(null);

  // Capture the ship we're fetching for when isFetching becomes true
  useEffect(() => {
    if (isFetching) {
      shipAtFetchStart.current = zoomToShip;
      // The ref may be overwritten by a newer zoom before this request settles
      const requestedShip = zoomToShip;

      getShipInfo(olSession)
        .then((data) => {
          setFetchedShipInfoResult(data);
        })
        .catch((error) => {
          console.error('Failed to fetch ship info:', error);
          reportRemoteZoomFailure(requestedShip, 'target-info-error', error);
        });
    }
  }, [isFetching, zoomToShip, olSession]);

  const processTargetInfo = async () => {
    if (!fetchedShipInfoResult) {
      return [];
    }

    const targetInfoResult = extractTargetInfos(fetchedShipInfoResult);
    fillMissingFieldsFromSearch(targetInfoResult, shipAtFetchStart.current);

    // If we found target info, return it immediately
    if (targetInfoResult.extractedTargetInfo.length > 0) {
      return [targetInfoResult];
    }

    // Guard clause - if no MMSI, return empty list
    if (!shipAtFetchStart.current?.mmsi) {
      reportRemoteZoomFailure(shipAtFetchStart.current, 'no-target-info');
      return [];
    }

    // Try to fetch by MMSI as fallback
    try {
      const queryPointByMmsi = await getTargetInfoByMMSI(
        olSession,
        Number(shipAtFetchStart.current.mmsi),
      );
      const targetInfoResultByMmsi = extractTargetInfos(queryPointByMmsi);
      fillMissingFieldsFromSearch(
        targetInfoResultByMmsi,
        shipAtFetchStart.current,
      );
      if (targetInfoResultByMmsi.extractedTargetInfo.length > 0) {
        return [targetInfoResultByMmsi];
      }
    } catch (error) {
      if (error instanceof InvalidMMSIError) {
        reportRemoteZoomFailure(shipAtFetchStart.current, 'invalid-mmsi');
        toast({
          title: messages.genericToastTitleWarning,
          description: 'Invalid MMSI number',
          variant: 'warning',
        });
        return [];
      }
      reportRemoteZoomFailure(
        shipAtFetchStart.current,
        'target-info-error',
        error,
      );
      throw error;
    }

    reportRemoteZoomFailure(shipAtFetchStart.current, 'no-target-info');
    toast({
      title: messages.genericToastTitleWarning,
      description: "Couldn't find vessel info",
      variant: 'warning',
    });

    return [];
  };

  useEffect(() => {
    // Guard clause - exit early if no fetched results
    if (!fetchedShipInfoResult) {
      return;
    }
    // Create a separate async function inside useEffect
    const handleProcessTargetInfo = async () => {
      try {
        const targetInfoList = await processTargetInfo();
        // On mobile: don't select vessel, only zoom to it
        if (!isMobile) {
          setTargetInfoDetailsList(targetInfoList);
        }
      } catch (error) {
        console.error('Failed to process target info:', error);
        Sentry.captureException(error);
      } finally {
        setIsFetching(false);
        setFetchedShipInfoResult(null);
        setZoomToShip(null);
      }
    };

    handleProcessTargetInfo();
  }, [
    fetchedShipInfoResult,
    zoomToShip,
    setIsFetching,
    setTargetInfoDetailsList,
    toast,
    messages,
    processTargetInfo,
    setZoomToShip,
    isMobile,
  ]);
}

export function useZoomAndSelectShip() {
  const { zoomToShip } = useShipSelection();
  const { olSession } = useSession();
  const { timeZone } = useTimeZoneContext();
  const [isFetchingShipInfo, setIsFetchingShipInfo] = useState(false);

  useEffect(() => {
    if (!zoomToShip) {
      setIsFetchingShipInfo(false);
    }
  }, [zoomToShip]);

  const { data: findTargetResult, error: findTargetError } =
    useFindTargetByProperties(zoomToShip);
  useReportRemoteFindTargetOutcome(zoomToShip, {
    data: findTargetResult,
    error: findTargetError,
  });

  const overlayProps = useOverlayAndMap(
    zoomToShip ? (findTargetResult ?? null) : null,
    zoomToShip,
  );
  useEffect(() => {
    const fetchOverlayData = async () => {
      if (overlayProps && zoomToShip) {
        try {
          await getOverlayMP(olSession, overlayProps, timeZone);

          // When filteredOut is true, the backend needs additional time to process
          // the ship data, especially when dealing with filtered/hidden ships that
          // may require extra validation or data retrieval steps
          const delayMs = zoomToShip.filteredOut ? 200 : 50;

          setTimeout(() => {
            setIsFetchingShipInfo(true);
          }, delayMs);
        } catch (error) {
          console.error('Failed to fetch overlay data:', error);
          reportRemoteZoomFailure(zoomToShip, 'overlay-error', error);
        }
      }
    };

    fetchOverlayData();
  }, [zoomToShip, olSession, overlayProps, timeZone]);

  useFetchShipInfo(isFetchingShipInfo, setIsFetchingShipInfo);
}
